import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx-js-style';
import { saveAs } from 'file-saver';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PlanGate from '../components/PlanGate';
import usePlan from '../hooks/usePlan';
import { ETAPAS_OBRA } from '../data/etapasObra';

function formatBRL(value) {
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}


// ============================================================
// ÍNDICES DE CONSUMO — Referências SINAPI / Coral / Suvinil / Quartzolit
// ============================================================

// Tinta: rendimento em m²/L por demão
const RENDIMENTO_TINTA = {
  'Tinta PVA':        { m2PorLitro: 10, demaos: 3 },
  'Tinta Acrílica':   { m2PorLitro: 10, demaos: 3 },
  'Látex PVA':        { m2PorLitro: 10, demaos: 3 },
  'Látex acrílica':   { m2PorLitro: 10, demaos: 3 },
  'Textura':          { m2PorLitro: 4,  demaos: 2 },
  'Epóxi':            { m2PorLitro: 8,  demaos: 2 },
  'Esmalte':          { m2PorLitro: 10, demaos: 2 },
};
const TINTA_DEFAULT = { m2PorLitro: 10, demaos: 3 };

// Argamassa colante: kg/m² (piso)
const CONSUMO_ARGAMASSA_PISO = {
  'Porcelanato Premium': 5.0,
  'Porcelanato Simples': 5.0,
  'Porcelanato':         5.0,
  'Cerâmica':            4.0,
  'Vinílico':            0,
  'Laminado':            0,
  'Cimentado':           0,
  'Madeira':             0,
};
const ARGAMASSA_PISO_DEFAULT = 4.0;

// Argamassa colante: kg/m² (revestimento parede)
const CONSUMO_ARGAMASSA_REV = {
  'Cerâmica':        4.0,
  'Porcelanato':     5.0,
  'Pedra natural':   5.0,
};

// Rejunte: kg/m²
const CONSUMO_REJUNTE_PISO = {
  'Porcelanato Premium': 0.5,
  'Porcelanato Simples': 0.5,
  'Porcelanato':         0.5,
  'Cerâmica':            0.6,
};
const CONSUMO_REJUNTE_REV = {
  'Cerâmica':        0.5,
  'Porcelanato':     0.4,
  'Pedra natural':   0.6,
};

// ============================================================
// TRAÇOS — desmembramento em componentes (kg/m² de parede)
// ============================================================

// Chapisco: traço 1:3 (cimento:areia) — consumo total ~5 kg/m²
const TRACO_CHAPISCO = { cimento: 1.25, areia: 3.75 };

// Emboço: traço 1:2:8 (cimento:cal:areia) — espessura ~2cm, ~30 kg/m²
const TRACO_EMBOCO = { cimento: 2.70, cal: 5.50, areia: 21.80 };

// Argamassa de assentamento: traço 1:2:8 — consumo ~10 kg/m²
const TRACO_ASSENTAMENTO = { cimento: 0.90, cal: 1.85, areia: 7.25 };

// Reboco / acabamento — por tipo selecionado
const TIPOS_REBOCO = {
  'Gesso liso':              { consumoM2: 4.0,  unidade: 'kg', componentes: { gesso: 4.0 } },
  'Massa corrida PVA':       { consumoM2: 3.0,  unidade: 'kg', componentes: { massa_corrida_pva: 3.0 } },
  'Massa corrida acrílica':  { consumoM2: 3.0,  unidade: 'kg', componentes: { massa_corrida_acrilica: 3.0 } },
  'Massa fina (cimento)':    { consumoM2: 15.0, unidade: 'kg', componentes: { cimento: 2.50, cal: 2.50, areia: 10.0 } },
};
const REBOCO_DEFAULT = 'Massa corrida PVA';

// Bloco cerâmico 9×19×19cm: ~25 un/m² (com 5% de perdas)
const CONSUMO_BLOCO_UN_M2 = 25;

// Selador: rendimento ~10 m²/L, 1 demão
const RENDIMENTO_SELADOR = 10;

// ============================================================
// COBERTURA — índices por tipo de telha
// ============================================================
const TIPOS_TELHA = {
  'Cerâmica colonial':       { unM2: 16, cumeiraML: 0.5, tipo: 'ceramica' },
  'Cerâmica romana':         { unM2: 14, cumeiraML: 0.5, tipo: 'ceramica' },
  'Cerâmica portuguesa':     { unM2: 16, cumeiraML: 0.5, tipo: 'ceramica' },
  'Concreto':                { unM2: 10, cumeiraML: 0.5, tipo: 'ceramica' },
  'Fibrocimento':            { chapasM2: 1.3, cumeiraML: 0.5, tipo: 'chapa' },
  'Metálica/galvanizada':    { chapasM2: 1.1, cumeiraML: 0.3, tipo: 'chapa' },
  'Sanduíche termoacústica': { chapasM2: 1.1, cumeiraML: 0.3, tipo: 'chapa' },
  'Laje impermeabilizada':   { mantaM2: 1.2, tipo: 'laje' },
};

// Madeiramento por m² de telhado (estrutura de madeira)
const MADEIRAMENTO = {
  ripas:   2.2,  // ml/m²
  caibros: 1.5,  // ml/m²
  tercas:  0.7,  // ml/m²
};

// Estrutura metálica: ~3.5 kg/m² de perfil
const ESTRUTURA_METALICA_KG_M2 = 3.5;

function calcularCobertura(cobertura) {
  if (!cobertura || !cobertura.tipo || !cobertura.areaM2) return null;

  const areaProjH = parseFloat(cobertura.areaM2) || 0;
  if (areaProjH <= 0) return null;

  const inclinacao = parseFloat(cobertura.inclinacao) || 30;
  // Área real do telhado = projeção horizontal / cos(ângulo)
  const anguloRad = Math.atan(inclinacao / 100);
  const fatorInclinacao = 1 / Math.cos(anguloRad);
  const areaReal = Math.round(areaProjH * fatorInclinacao * 100) / 100;

  const config = TIPOS_TELHA[cobertura.tipo];
  if (!config) return null;

  const result = {
    tipo: cobertura.tipo,
    areaProjecao: areaProjH,
    areaReal,
    inclinacao,
    estrutura: cobertura.estrutura || 'madeira',
  };

  // Telhas/chapas
  if (config.tipo === 'ceramica') {
    result.telhasUn = Math.ceil(areaReal * config.unM2 * 1.05); // +5% perdas
  } else if (config.tipo === 'chapa') {
    result.chapasUn = Math.ceil(areaReal * config.chapasM2);
  } else if (config.tipo === 'laje') {
    result.mantaM2 = Math.ceil(areaReal * config.mantaM2);
  }

  // Cumeeira
  if (config.cumeiraML) {
    result.cumeiraML = Math.ceil(areaReal * config.cumeiraML);
  }

  // Calha e rufo estimados
  result.calhaML = Math.ceil(Math.sqrt(areaProjH) * 2); // estimativa perímetro
  result.rufoML = Math.ceil(Math.sqrt(areaProjH));

  // Estrutura
  if (cobertura.estrutura === 'madeira') {
    result.ripasML = Math.ceil(areaReal * MADEIRAMENTO.ripas);
    result.caibrosML = Math.ceil(areaReal * MADEIRAMENTO.caibros);
    result.tercasML = Math.ceil(areaReal * MADEIRAMENTO.tercas);
  } else if (cobertura.estrutura === 'metalica') {
    result.perfilMetalicoKg = Math.ceil(areaReal * ESTRUTURA_METALICA_KG_M2);
  }

  return result;
}

function parseAlturaRev(alturaStr) {
  if (!alturaStr) return 1.50;
  if (alturaStr.includes('teto')) return 2.80;
  if (alturaStr.includes('2,10') || alturaStr.includes('2.10')) return 2.10;
  if (alturaStr.includes('1,50') || alturaStr.includes('1.50')) return 1.50;
  return 1.50;
}

// ============================================================
// CÁLCULO DE QUANTITATIVOS POR CÔMODO
// ============================================================

function calcularQuantitativos(comodos, areaTotal) {
  if (comodos && comodos.length > 0) {
    return comodos.map((c) => {
      const area = parseFloat(c.area) || 0;
      const pd = parseFloat(c.pe_direito) || 2.80;
      const perimetro = c.perimetro_ml || Math.round(Math.sqrt(area) * 4 * 100) / 100;
      const acabamento = c.acabamento || {};
      const especiais = c.especiais || {};
      const nome = (c.nome || '').toLowerCase();

      // === PISO ===
      let tipoPiso = acabamento.tipoPiso || '';
      if (!tipoPiso) {
        if (nome.includes('sala') || nome.includes('estar') || nome.includes('cozinha') || nome.includes('copa')) tipoPiso = 'Porcelanato';
        else if (nome.includes('quarto') || nome.includes('dorm') || nome.includes('suite') || nome.includes('suíte')) tipoPiso = 'Laminado';
        else tipoPiso = 'Cerâmica';
      }
      const pisoM2 = area;
      const argamassaPisoKg = Math.ceil(pisoM2 * (CONSUMO_ARGAMASSA_PISO[tipoPiso] ?? ARGAMASSA_PISO_DEFAULT));
      const rejuntePisoKg = Math.ceil(pisoM2 * (CONSUMO_REJUNTE_PISO[tipoPiso] || 0));

      // === ESQUADRIAS ===
      const esqs = c.esquadrias || [];
      const vaosEsquadrias = esqs.reduce((sum, e) => {
        const dims = (e.dimensoes || '0,80x2,10').replace(/,/g, '.').split(/[xX×]/);
        const larg = parseFloat(dims[0]) || 0.8;
        const alt = parseFloat(dims[1]) || 2.1;
        const qty = parseInt(e.quantidade) || 1;
        return sum + (larg * alt * qty);
      }, 0);
      const portas = esqs.filter(e => (e.tipo || '').toLowerCase().includes('porta'));
      const janelas = esqs.filter(e => !(e.tipo || '').toLowerCase().includes('porta'));
      const totalPortas = portas.reduce((s, e) => s + (parseInt(e.quantidade) || 1), 0);
      const totalJanelas = janelas.reduce((s, e) => s + (parseInt(e.quantidade) || 1), 0);

      // === PAREDE ===
      const areaParede = Math.max(0, Math.round((perimetro * pd - vaosEsquadrias) * 100) / 100);

      // === REVESTIMENTO CERÂMICO DE PAREDE ===
      const tipoRevParede = acabamento.tipoRevParede || '';
      const alturaRevStr = acabamento.alturaRevestimento || '';
      const alturaRev = parseAlturaRev(alturaRevStr);
      const revCeramicos = ['Cerâmica', 'Porcelanato', 'Pedra natural'];
      const temRevCeramico = revCeramicos.includes(tipoRevParede);
      let areaPintura = areaParede;
      let areaRevCeramicoM2 = 0;
      if (temRevCeramico) {
        areaRevCeramicoM2 = Math.round(perimetro * alturaRev * 100) / 100;
        areaPintura = Math.max(0, areaParede - areaRevCeramicoM2);
      }

      // === TINTA: Litros = (área ÷ rendimento) × demãos ===
      const rendTinta = RENDIMENTO_TINTA[tipoRevParede] || TINTA_DEFAULT;
      const tintaLitros = areaPintura > 0
        ? Math.ceil((areaPintura / rendTinta.m2PorLitro) * rendTinta.demaos)
        : 0;

      // === ARGAMASSA + REJUNTE REVESTIMENTO PAREDE ===
      const argamassaRevKg = temRevCeramico
        ? Math.ceil(areaRevCeramicoM2 * (CONSUMO_ARGAMASSA_REV[tipoRevParede] || 4.0))
        : 0;
      const rejunteRevKg = temRevCeramico
        ? Math.ceil(areaRevCeramicoM2 * (CONSUMO_REJUNTE_REV[tipoRevParede] || 0.5))
        : 0;

      // === REBOCO — por tipo selecionado ===
      const tipoReboco = acabamento.tipoReboco || REBOCO_DEFAULT;
      const rebocoConfig = TIPOS_REBOCO[tipoReboco] || TIPOS_REBOCO[REBOCO_DEFAULT];
      const rebocoKgTotal = areaPintura > 0 ? Math.ceil(areaPintura * rebocoConfig.consumoM2) : 0;

      // Desmembrar reboco em componentes
      const rebocoComponentes = {};
      if (areaPintura > 0) {
        for (const [comp, idx] of Object.entries(rebocoConfig.componentes)) {
          rebocoComponentes[comp] = Math.ceil(areaPintura * idx);
        }
      }

      // === ALVENARIA — desmembrada por componente (2 lados da parede) ===
      const blocoUn = Math.ceil(areaParede * CONSUMO_BLOCO_UN_M2);

      // Chapisco (2 lados)
      const chapiscoCimento = Math.ceil(areaParede * TRACO_CHAPISCO.cimento * 2);
      const chapiscoAreia = Math.ceil(areaParede * TRACO_CHAPISCO.areia * 2);

      // Emboço (2 lados)
      const embocoCimento = Math.ceil(areaParede * TRACO_EMBOCO.cimento * 2);
      const embocoCal = Math.ceil(areaParede * TRACO_EMBOCO.cal * 2);
      const embocoAreia = Math.ceil(areaParede * TRACO_EMBOCO.areia * 2);

      // Arg. assentamento (1 lado — entre blocos)
      const assentCimento = Math.ceil(areaParede * TRACO_ASSENTAMENTO.cimento);
      const assentCal = Math.ceil(areaParede * TRACO_ASSENTAMENTO.cal);
      const assentAreia = Math.ceil(areaParede * TRACO_ASSENTAMENTO.areia);

      // Selador
      const seladorLitros = areaPintura > 0 ? Math.ceil(areaPintura / RENDIMENTO_SELADOR) : 0;

      // === TOTAIS CONSOLIDADOS por componente ===
      const totalCimento = chapiscoCimento + embocoCimento + assentCimento + (rebocoComponentes.cimento || 0);
      const totalCal = embocoCal + assentCal + (rebocoComponentes.cal || 0);
      const totalAreia = chapiscoAreia + embocoAreia + assentAreia + (rebocoComponentes.areia || 0);

      // === TOTAIS ===
      const argamassaTotalKg = argamassaPisoKg + argamassaRevKg;
      const rejunteTotalKg = rejuntePisoKg + rejunteRevKg;

      // === RODAPÉ ===
      const tipoRodape = acabamento.tipoRodape || 'Sem rodapé';
      const largVaosPorta = portas.reduce((sum, e) => {
        const dims = (e.dimensoes || '0,80x2,10').replace(/,/g, '.').split(/[xX×]/);
        const larg = parseFloat(dims[0]) || 0.8;
        const qty = parseInt(e.quantidade) || 1;
        return sum + (larg * qty);
      }, 0);
      const rodapeML = tipoRodape !== 'Sem rodapé'
        ? Math.max(0, Math.round((perimetro - largVaosPorta) * 100) / 100)
        : 0;

      // === FORRO ===
      const tipoForro = acabamento.forro || '';
      const forroM2 = tipoForro && tipoForro !== 'Sem forro' ? area : 0;

      return {
        nome: c.nome,
        area,
        tipoPiso, pisoM2,
        tipoRodape, rodapeML,
        tipoForro, forroM2,
        tipoRevParede, alturaRev: alturaRevStr,
        areaParede, areaPintura, areaRevCeramicoM2,
        tintaLitros,
        argamassaPisoKg, argamassaRevKg, argamassaTotalKg,
        rejuntePisoKg, rejunteRevKg, rejunteTotalKg,
        rebocoKgTotal, tipoReboco, rebocoComponentes,
        blocoUn,
        chapisco: { cimento: chapiscoCimento, areia: chapiscoAreia },
        emboco: { cimento: embocoCimento, cal: embocoCal, areia: embocoAreia },
        assentamento: { cimento: assentCimento, cal: assentCal, areia: assentAreia },
        seladorLitros,
        totalCimento, totalCal, totalAreia,
        perimetroML: perimetro, peDireito: pd,
        totalPortas, totalJanelas,
        esquadrias: esqs.map(e => ({
          tipo: e.tipo || 'Porta',
          quantidade: parseInt(e.quantidade) || 1,
          material: e.material || '',
          dimensoes: e.dimensoes || '',
        })),
        bancada: especiais.bancada || '',
        pia: especiais.pia || '',
        soleira: especiais.soleira || '',
        peitoril: especiais.peitoril || '',
        real: true,
      };
    });
  }

  return estimateGenerico(areaTotal);
}

function estimateGenerico(areaTotal) {
  const rooms = [
    { nome: 'Sala / Estar', area: Math.round(areaTotal * 0.18), tipoPiso: 'Porcelanato' },
    { nome: 'Cozinha', area: Math.round(areaTotal * 0.12), tipoPiso: 'Porcelanato' },
    { nome: 'Quarto 1', area: Math.round(areaTotal * 0.14), tipoPiso: 'Laminado' },
    { nome: 'Quarto 2', area: Math.round(areaTotal * 0.12), tipoPiso: 'Laminado' },
    { nome: 'Banheiro Social', area: Math.round(areaTotal * 0.06), tipoPiso: 'Cerâmica' },
    { nome: 'Banheiro Suíte', area: Math.round(areaTotal * 0.05), tipoPiso: 'Cerâmica' },
    { nome: 'Área de Serviço', area: Math.round(areaTotal * 0.06), tipoPiso: 'Cerâmica' },
    { nome: 'Circulação / Hall', area: Math.round(areaTotal * 0.07), tipoPiso: 'Porcelanato' },
  ];
  return rooms.map((room) => {
    const p = Math.round(Math.sqrt(room.area) * 4 * 100) / 100;
    const ap = Math.round(p * 2.80 * 100) / 100;
    const argP = Math.ceil(room.area * (CONSUMO_ARGAMASSA_PISO[room.tipoPiso] ?? ARGAMASSA_PISO_DEFAULT));
    const rejP = Math.ceil(room.area * (CONSUMO_REJUNTE_PISO[room.tipoPiso] || 0));
    const tinta = Math.ceil((ap / TINTA_DEFAULT.m2PorLitro) * TINTA_DEFAULT.demaos);
    const rCfg = TIPOS_REBOCO[REBOCO_DEFAULT];
    const rKg = Math.ceil(ap * rCfg.consumoM2);
    const rComp = {};
    for (const [c, v] of Object.entries(rCfg.componentes)) rComp[c] = Math.ceil(ap * v);
    return {
      ...room, pisoM2: room.area,
      tipoRodape: 'Sem rodapé', rodapeML: 0,
      tipoForro: '', forroM2: 0,
      tipoRevParede: '', alturaRev: '',
      areaParede: ap, areaPintura: ap, areaRevCeramicoM2: 0,
      tintaLitros: tinta,
      argamassaPisoKg: argP, argamassaRevKg: 0, argamassaTotalKg: argP,
      rejuntePisoKg: rejP, rejunteRevKg: 0, rejunteTotalKg: rejP,
      rebocoKgTotal: rKg, tipoReboco: REBOCO_DEFAULT, rebocoComponentes: rComp,
      blocoUn: Math.ceil(ap * CONSUMO_BLOCO_UN_M2),
      chapisco: { cimento: Math.ceil(ap * TRACO_CHAPISCO.cimento * 2), areia: Math.ceil(ap * TRACO_CHAPISCO.areia * 2) },
      emboco: { cimento: Math.ceil(ap * TRACO_EMBOCO.cimento * 2), cal: Math.ceil(ap * TRACO_EMBOCO.cal * 2), areia: Math.ceil(ap * TRACO_EMBOCO.areia * 2) },
      assentamento: { cimento: Math.ceil(ap * TRACO_ASSENTAMENTO.cimento), cal: Math.ceil(ap * TRACO_ASSENTAMENTO.cal), areia: Math.ceil(ap * TRACO_ASSENTAMENTO.areia) },
      seladorLitros: Math.ceil(ap / RENDIMENTO_SELADOR),
      totalCimento: 0, totalCal: 0, totalAreia: 0,
      perimetroML: p, peDireito: 2.80,
      totalPortas: 0, totalJanelas: 0, esquadrias: [],
      bancada: '', pia: '', soleira: '', peitoril: '',
      real: false,
    };
  });
}

function OrcamentoResultadoCub() {
  const navigate = useNavigate();
  const { plan } = usePlan();
  const [visible, setVisible] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [showEtapas, setShowEtapas] = useState(false);
  const [showMateriais, setShowMateriais] = useState(false);

  useEffect(() => {
    try {
      const stored = localStorage.getItem('orcai_cub_resultado');
      if (stored) {
        setResultado(JSON.parse(stored));
      } else {
        navigate('/orcamento-cub');
        return;
      }
    } catch {
      navigate('/orcamento-cub');
      return;
    }
    const timer = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(timer);
  }, [navigate]);

  const etapasCalc = useMemo(() => {
    if (!resultado) return [];
    return ETAPAS_OBRA.map((e) => ({
      ...e,
      valor: resultado.total * (e.percentual / 100),
    }));
  }, [resultado]);

  const materiaisCalc = useMemo(() => {
    if (!resultado) return [];
    return calcularQuantitativos(
      resultado.comodos || [],
      resultado.areaPrincipal || resultado.areaTotal || 100
    );
  }, [resultado]);

  const coberturaCalc = useMemo(() => {
    if (!resultado || !resultado.cobertura) return null;
    return calcularCobertura(resultado.cobertura);
  }, [resultado]);

  const handleExportExcel = () => {
    if (!resultado) return;
    const r = resultado;
    const dateStr = new Date().toISOString().slice(0, 10);
    const orcId = `ORC-${Date.now().toString(36).toUpperCase()}`;

    // === STYLES ===
    const navy = { fill: { fgColor: { rgb: "1E3A5F" } }, font: { bold: true, color: { rgb: "FFFFFF" }, sz: 11 }, alignment: { horizontal: "left" } };
    // eslint-disable-next-line no-unused-vars
    const navyCenter = { ...navy, alignment: { horizontal: "center" } };
    const orange = { fill: { fgColor: { rgb: "F97316" } }, font: { bold: true, color: { rgb: "FFFFFF" }, sz: 11 }, alignment: { horizontal: "left" } };
    const orangeCenter = { ...orange, alignment: { horizontal: "center" } };
    const green = { fill: { fgColor: { rgb: "10B981" } }, font: { bold: true, color: { rgb: "FFFFFF" }, sz: 10 }, alignment: { horizontal: "left" } };
    const blue = { fill: { fgColor: { rgb: "3B82F6" } }, font: { bold: true, color: { rgb: "FFFFFF" }, sz: 10 }, alignment: { horizontal: "left" } };
    const purple = { fill: { fgColor: { rgb: "8B5CF6" } }, font: { bold: true, color: { rgb: "FFFFFF" }, sz: 10 }, alignment: { horizontal: "left" } };
    const lightGray = { fill: { fgColor: { rgb: "F1F5F9" } } };
    const bold = { font: { bold: true, sz: 11 } };
    const boldRight = { font: { bold: true, sz: 11 }, alignment: { horizontal: "right" } };
    const sectionHeader = { fill: { fgColor: { rgb: "E2E8F0" } }, font: { bold: true, sz: 10 } };
    const roomHeader = { fill: { fgColor: { rgb: "DBEAFE" } }, font: { bold: true, sz: 10, color: { rgb: "1E40AF" } } };
    const totalRow = { fill: { fgColor: { rgb: "FFF7ED" } }, font: { bold: true, sz: 11, color: { rgb: "C2410C" } } };

    // Helper: styled cell
    const sc = (val, style) => ({ v: val, t: typeof val === 'number' ? 'n' : 's', s: style });

    // Helper: apply styles to range
    // eslint-disable-next-line no-unused-vars
    const styleRange = (ws, startRow, startCol, endCol, style) => {
      for (let c = startCol; c <= endCol; c++) {
        const addr = XLSX.utils.encode_cell({ r: startRow, c });
        if (!ws[addr]) ws[addr] = { v: '', t: 's' };
        ws[addr].s = style;
      }
    };

    // ===== ABA 1: RESUMO =====
    const resumoData = [
      [sc('QuantoCustaObra — Estimativa de Custo de Obra', navy), sc('', navy), sc('', navy)],
      [sc(`Gerado em: ${new Date().toLocaleString('pt-BR')}`, lightGray), '', sc(`Ref: ${orcId}`, { ...lightGray, font: { sz: 10, italic: true } })],
      [],
      [sc('DADOS DO PROJETO', sectionHeader), sc('', sectionHeader), sc('', sectionHeader)],
      ...(r.nomeProjeto ? [['Nome do Projeto', sc(r.nomeProjeto, bold)]] : []),
      ['Tipo de Projeto', `${r.tipoCodigo} - ${r.tipoLabel}`],
      ['Estado', `${r.estadoNome} (${r.estadoSigla})`],
      ['CUB Base', formatBRL(r.cubBase) + '/m²'],
      ['BDI', `${r.bdiPercent}%`],
      ['Referência CUB', r.dataRef],
      ...(r.padraoAcabamento && Object.values(r.padraoAcabamento).some(v => v) ? [
        [],
        [sc('PADRÃO DE ACABAMENTO', sectionHeader), sc('', sectionHeader), sc('', sectionHeader)],
        ...(r.padraoAcabamento.tipoPiso ? [['Tipo de Piso', r.padraoAcabamento.tipoPiso]] : []),
        ...(r.padraoAcabamento.peDireito ? [['Pé Direito', r.padraoAcabamento.peDireito]] : []),
        ...(r.padraoAcabamento.forro ? [['Forro', r.padraoAcabamento.forro]] : []),
        ...(r.padraoAcabamento.revestimentoParedes ? [['Revestimento de Paredes', r.padraoAcabamento.revestimentoParedes]] : []),
        ...(r.padraoAcabamento.alturaRevestimento ? [['Altura do Revestimento', r.padraoAcabamento.alturaRevestimento]] : []),
      ] : []),
      [],
      [sc('CUSTO TOTAL ESTIMADO', totalRow), sc(formatBRL(r.total), totalRow), sc('', totalRow)],
      [],
      [sc('RESUMO POR CATEGORIA', sectionHeader), sc('', sectionHeader), sc('Valor (R$)', { ...sectionHeader, alignment: { horizontal: 'right' } })],
      ['Subtotal Áreas', '', formatBRL(r.subtotalAreas)],
      ...(r.subtotalExtras > 0 ? [['Subtotal Extras', '', formatBRL(r.subtotalExtras)]] : []),
      ['Subtotal Geral', '', formatBRL(r.subtotalGeral)],
      [`BDI (${r.bdiPercent}%)`, '', formatBRL(r.bdiValor)],
      [sc('TOTAL', bold), sc('', bold), sc(formatBRL(r.total), boldRight)],
    ];
    const wsResumo = XLSX.utils.aoa_to_sheet(resumoData);
    wsResumo['!cols'] = [{ wch: 32 }, { wch: 35 }, { wch: 22 }];
    wsResumo['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 2 } }];

    // ===== ABA 2: DETALHAMENTO =====
    const detData = [
      [sc('QuantoCustaObra — Detalhamento do Orçamento', navy), sc('', navy), sc('', navy)],
      [],
      [sc('ÁREAS', sectionHeader), sc('', sectionHeader), sc('', sectionHeader)],
      [sc('Item', { ...sectionHeader, font: { bold: true, sz: 9 } }), sc('Cálculo', { ...sectionHeader, font: { bold: true, sz: 9 } }), sc('Valor (R$)', { ...sectionHeader, font: { bold: true, sz: 9 }, alignment: { horizontal: 'right' } })],
      ...r.linhasAreas.map((l) => [l.item, l.calculo, formatBRL(l.valor)]),
      [sc('Subtotal Áreas', bold), '', sc(formatBRL(r.subtotalAreas), boldRight)],
      [],
    ];
    if (r.linhasExtras.length > 0) {
      detData.push([sc('ITENS EXTRAS', sectionHeader), sc('', sectionHeader), sc('', sectionHeader)]);
      detData.push([sc('Item', { ...sectionHeader, font: { bold: true, sz: 9 } }), sc('Cálculo', { ...sectionHeader, font: { bold: true, sz: 9 } }), sc('Valor (R$)', { ...sectionHeader, font: { bold: true, sz: 9 }, alignment: { horizontal: 'right' } })]);
      r.linhasExtras.forEach((l) => detData.push([l.item, l.calculo, formatBRL(l.valor)]));
      detData.push([sc('Subtotal Extras', bold), '', sc(formatBRL(r.subtotalExtras), boldRight)]);
      detData.push([]);
    }
    detData.push([sc('Subtotal Geral', bold), '', sc(formatBRL(r.subtotalGeral), boldRight)]);
    detData.push([sc(`BDI (${r.bdiPercent}%)`, bold), '', sc(formatBRL(r.bdiValor), boldRight)]);
    detData.push([]);
    detData.push([sc('TOTAL ESTIMADO', totalRow), sc('', totalRow), sc(formatBRL(r.total), { ...totalRow, alignment: { horizontal: 'right' } })]);
    const wsDet = XLSX.utils.aoa_to_sheet(detData);
    wsDet['!cols'] = [{ wch: 30 }, { wch: 40 }, { wch: 22 }];
    wsDet['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 2 } }];

    // ===== ABA 3: ETAPAS =====
    const etapasData = [
      [sc('QuantoCustaObra — Breakdown por Etapas', orange), sc('', orange), sc('', orange)],
      [],
      [sc('Etapa', sectionHeader), sc('Percentual', { ...sectionHeader, alignment: { horizontal: 'center' } }), sc('Valor Estimado (R$)', { ...sectionHeader, alignment: { horizontal: 'right' } })],
      ...etapasCalc.map((e) => [e.nome, `${e.percentual}%`, formatBRL(e.valor)]),
      [],
      [sc('TOTAL', bold), sc('100%', { ...bold, alignment: { horizontal: 'center' } }), sc(formatBRL(r.total), boldRight)],
    ];
    const wsEtapas = XLSX.utils.aoa_to_sheet(etapasData);
    wsEtapas['!cols'] = [{ wch: 38 }, { wch: 15 }, { wch: 22 }];
    wsEtapas['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 2 } }];

    // ===== ABA 4: MATERIAIS POR CÔMODO (LAYOUT VERTICAL) =====
    const matData = [
      [sc('QuantoCustaObra — Quantitativo de Materiais por Cômodo', blue), sc('', blue), sc('', blue), sc('', blue)],
      [],
    ];

    materiaisCalc.forEach((m) => {
      matData.push([sc(`${m.nome}`, roomHeader), sc(`${m.area} m²`, roomHeader), sc('', roomHeader), sc('', roomHeader)]);
      matData.push([sc('Categoria', sectionHeader), sc('Material', sectionHeader), sc('Qtd', { ...sectionHeader, alignment: { horizontal: 'right' } }), sc('Un', { ...sectionHeader, alignment: { horizontal: 'center' } })]);

      // Piso
      matData.push(['Piso', m.tipoPiso, m.pisoM2, 'm²']);
      if (m.argamassaPisoKg > 0) matData.push(['', 'Arg. colante (piso)', m.argamassaPisoKg, 'kg']);
      if (m.rejuntePisoKg > 0) matData.push(['', 'Rejunte (piso)', m.rejuntePisoKg, 'kg']);

      // Parede
      if (m.areaPintura > 0) {
        matData.push(['Pintura', 'Tinta', m.tintaLitros, 'L']);
        matData.push(['', 'Selador', m.seladorLitros || 0, 'L']);
      }
      if (m.rebocoKgTotal > 0) matData.push(['Reboco', m.tipoReboco || 'Massa corrida PVA', m.rebocoKgTotal, 'kg']);

      // Revestimento parede
      if (m.areaRevCeramicoM2 > 0) {
        matData.push(['Rev. parede', m.tipoRevParede, m.areaRevCeramicoM2, 'm²']);
        if (m.argamassaRevKg > 0) matData.push(['', 'Arg. colante (parede)', m.argamassaRevKg, 'kg']);
        if (m.rejunteRevKg > 0) matData.push(['', 'Rejunte (parede)', m.rejunteRevKg, 'kg']);
      }

      // Alvenaria
      if (m.blocoUn > 0) matData.push(['Alvenaria', 'Bloco cerâmico 9×19×19', m.blocoUn, 'un']);

      // Rodapé / Forro
      if (m.rodapeML > 0) matData.push(['Acabamento', `Rodapé (${m.tipoRodape})`, m.rodapeML, 'ml']);
      if (m.forroM2 > 0) matData.push(['Acabamento', `Forro (${m.tipoForro})`, m.forroM2, 'm²']);

      // Esquadrias
      if (m.totalPortas > 0) matData.push(['Esquadrias', 'Portas', m.totalPortas, 'un']);
      if (m.totalJanelas > 0) matData.push(['Esquadrias', 'Janelas', m.totalJanelas, 'un']);

      matData.push([]); // blank row between rooms
    });

    const wsMat = XLSX.utils.aoa_to_sheet(matData);
    wsMat['!cols'] = [{ wch: 18 }, { wch: 30 }, { wch: 14 }, { wch: 8 }];
    wsMat['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 3 } }];

    // ===== ABA 5: MTO CONSOLIDADO =====
    const sumField = (field) => materiaisCalc.reduce((s, m) => s + (parseFloat(m[field]) || 0), 0);
    const sumNested = (parent, child) => materiaisCalc.reduce((s, m) => s + (m[parent]?.[child] || 0), 0);
    const totalCimento = sumField('totalCimento');
    const totalCal = sumField('totalCal');
    const totalAreia = sumField('totalAreia');

    const mtoData = [
      [sc('QuantoCustaObra — Material Take-Off Consolidado', navy), sc('', navy), sc('', navy), sc('', navy)],
      [],
      // INSUMOS BASE - highlight
      [sc('INSUMOS BASE', orange), sc('Quantidade', orangeCenter), sc('Unidade', orangeCenter), sc('Equivalência', orange)],
      [sc('Cimento', bold), sc(totalCimento, boldRight), 'kg', `${Math.ceil(totalCimento / 50)} sacos de 50kg`],
      [sc('Cal', bold), sc(totalCal, boldRight), 'kg', `${Math.ceil(totalCal / 20)} sacos de 20kg`],
      [sc('Areia', bold), sc(totalAreia, boldRight), 'kg', `${(totalAreia / 1500).toFixed(1)} m³`],
      [],
      // ALVENARIA
      [sc('ALVENARIA', green), sc('', green), sc('', green), sc('', green)],
      ['Bloco cerâmico 9×19×19', sumField('blocoUn'), 'un', ''],
      [],
      ['Arg. assentamento (1:2:8)', '', '', ''],
      [sc('  Cimento', lightGray), sc(sumNested('assentamento', 'cimento'), lightGray), sc('kg', lightGray), sc('', lightGray)],
      [sc('  Cal', lightGray), sc(sumNested('assentamento', 'cal'), lightGray), sc('kg', lightGray), sc('', lightGray)],
      [sc('  Areia', lightGray), sc(sumNested('assentamento', 'areia'), lightGray), sc('kg', lightGray), sc('', lightGray)],
      [],
      ['Chapisco (1:3)', '', '', ''],
      [sc('  Cimento', lightGray), sc(sumNested('chapisco', 'cimento'), lightGray), sc('kg', lightGray), sc('', lightGray)],
      [sc('  Areia', lightGray), sc(sumNested('chapisco', 'areia'), lightGray), sc('kg', lightGray), sc('', lightGray)],
      [],
      ['Emboço (1:2:8)', '', '', ''],
      [sc('  Cimento', lightGray), sc(sumNested('emboco', 'cimento'), lightGray), sc('kg', lightGray), sc('', lightGray)],
      [sc('  Cal', lightGray), sc(sumNested('emboco', 'cal'), lightGray), sc('kg', lightGray), sc('', lightGray)],
      [sc('  Areia', lightGray), sc(sumNested('emboco', 'areia'), lightGray), sc('kg', lightGray), sc('', lightGray)],
      [],
      // REVESTIMENTOS
      [sc('REVESTIMENTOS', blue), sc('', blue), sc('', blue), sc('', blue)],
      ['Piso (total)', sumField('pisoM2').toFixed(1), 'm²', ''],
      ['Arg. colante (piso)', sumField('argamassaPisoKg'), 'kg', ''],
      ['Rejunte (piso)', sumField('rejuntePisoKg'), 'kg', ''],
      ['Rev. parede cerâmico', sumField('areaRevCeramicoM2').toFixed(1), 'm²', ''],
      ['Arg. colante (parede)', sumField('argamassaRevKg'), 'kg', ''],
      ['Rejunte (parede)', sumField('rejunteRevKg'), 'kg', ''],
      ['Rodapé', sumField('rodapeML').toFixed(1), 'ml', ''],
      [],
      // PINTURA E ACABAMENTO
      [sc('PINTURA E ACABAMENTO', purple), sc('', purple), sc('', purple), sc('', purple)],
      ['Área de pintura', sumField('areaPintura').toFixed(1), 'm²', ''],
      ['Selador', sumField('seladorLitros'), 'L', ''],
      ['Tinta', sumField('tintaLitros'), 'L', ''],
      [`Reboco (${materiaisCalc[0]?.tipoReboco || 'Massa corrida PVA'})`, sumField('rebocoKgTotal'), 'kg', ''],
      ['Forro', sumField('forroM2').toFixed(1), 'm²', ''],
      [],
      // ESQUADRIAS
      [sc('ESQUADRIAS', { fill: { fgColor: { rgb: "6366F1" } }, font: { bold: true, color: { rgb: "FFFFFF" }, sz: 10 } }), sc('', { fill: { fgColor: { rgb: "6366F1" } } }), sc('', { fill: { fgColor: { rgb: "6366F1" } } }), sc('', { fill: { fgColor: { rgb: "6366F1" } } })],
      ['Portas', sumField('totalPortas'), 'un', ''],
      ['Janelas', sumField('totalJanelas'), 'un', ''],
    ];

    // Cobertura
    if (coberturaCalc) {
      mtoData.push([]);
      mtoData.push([sc(`COBERTURA — ${coberturaCalc.tipo}`, { fill: { fgColor: { rgb: "EC4899" } }, font: { bold: true, color: { rgb: "FFFFFF" }, sz: 10 } }), sc('', { fill: { fgColor: { rgb: "EC4899" } } }), sc('', { fill: { fgColor: { rgb: "EC4899" } } }), sc('', { fill: { fgColor: { rgb: "EC4899" } } })]);
      mtoData.push(['Área projeção', coberturaCalc.areaProjecao, 'm²', '']);
      mtoData.push([`Área real (incl. ${coberturaCalc.inclinacao}%)`, coberturaCalc.areaReal, 'm²', '']);
      if (coberturaCalc.telhasUn) mtoData.push(['Telhas (incl. 5% perdas)', coberturaCalc.telhasUn, 'un', '']);
      if (coberturaCalc.chapasUn) mtoData.push(['Chapas', coberturaCalc.chapasUn, 'un', '']);
      if (coberturaCalc.mantaM2) mtoData.push(['Manta asfáltica', coberturaCalc.mantaM2, 'm²', '']);
      if (coberturaCalc.cumeiraML) mtoData.push(['Cumeeira', coberturaCalc.cumeiraML, 'ml', '']);
      mtoData.push(['Calha', coberturaCalc.calhaML, 'ml', '']);
      mtoData.push(['Rufo', coberturaCalc.rufoML, 'ml', '']);
      if (coberturaCalc.estrutura === 'madeira') {
        mtoData.push([sc('  Ripas', lightGray), sc(coberturaCalc.ripasML, lightGray), sc('ml', lightGray), sc('', lightGray)]);
        mtoData.push([sc('  Caibros', lightGray), sc(coberturaCalc.caibrosML, lightGray), sc('ml', lightGray), sc('', lightGray)]);
        mtoData.push([sc('  Terças', lightGray), sc(coberturaCalc.tercasML, lightGray), sc('ml', lightGray), sc('', lightGray)]);
      }
      if (coberturaCalc.estrutura === 'metalica') {
        mtoData.push([sc('  Perfil metálico', lightGray), sc(coberturaCalc.perfilMetalicoKg, lightGray), sc('kg', lightGray), sc('', lightGray)]);
      }
    }

    mtoData.push([]);
    mtoData.push([sc('* Traços: chapisco 1:3, emboço 1:2:8, assentamento 1:2:8. Validar com projeto executivo.', { font: { italic: true, sz: 9, color: { rgb: "6B7280" } } })]);

    const wsMTO = XLSX.utils.aoa_to_sheet(mtoData);
    wsMTO['!cols'] = [{ wch: 35 }, { wch: 18 }, { wch: 10 }, { wch: 25 }];
    wsMTO['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 3 } }];

    // ===== MONTAR WORKBOOK =====
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, wsResumo, 'Resumo');
    XLSX.utils.book_append_sheet(wb, wsDet, 'Detalhamento');
    XLSX.utils.book_append_sheet(wb, wsEtapas, 'Etapas');
    XLSX.utils.book_append_sheet(wb, wsMat, 'Materiais');
    XLSX.utils.book_append_sheet(wb, wsMTO, 'MTO Consolidado');
    const buffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const fileName = r.nomeProjeto
      ? `Orcamento-${r.nomeProjeto.replace(/\s+/g, '-')}-${dateStr}.xlsx`
      : `Orcamento-QCO-${dateStr}.xlsx`;
    saveAs(blob, fileName);
  };

  const handleExportPDF = () => {
    if (!resultado) return;
    const r = resultado;
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15;
    const orcId = `ORC-${Date.now().toString(36).toUpperCase()}`;
    let y = 0;

    // Helper: check if needs new page
    const checkPage = (needed = 40) => {
      if (y > pageHeight - needed) {
        doc.addPage();
        y = 20;
      }
    };

    // Helper: section title
    const sectionTitle = (text, color = [30, 58, 95]) => {
      checkPage(30);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...color);
      doc.text(text, margin, y);
      y += 5;
    };

    // Helper: sum fields
    const sumField = (field) => materiaisCalc.reduce((s, m) => s + (parseFloat(m[field]) || 0), 0);
    const sumNested = (parent, child) => materiaisCalc.reduce((s, m) => s + (m[parent]?.[child] || 0), 0);

    // === PAGE 1 — DASHBOARD EXECUTIVO ===

    // Navy header
    doc.setFillColor(30, 58, 95);
    doc.rect(0, 0, pageWidth, 32, 'F');

    // QCO badge (orange square)
    doc.setFillColor(249, 115, 22);
    doc.roundedRect(margin, 7, 18, 18, 3, 3, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.text('QCO', margin + 9, 18, { align: 'center' });

    // Brand name
    doc.setFontSize(14);
    doc.text('QuantoCustaObra', margin + 22, 18);

    // Date + ID (right)
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text(new Date().toLocaleDateString('pt-BR'), pageWidth - margin, 13, { align: 'right' });
    doc.setFont('helvetica', 'bold');
    doc.text(orcId, pageWidth - margin, 21, { align: 'right' });

    // Orange accent line
    doc.setFillColor(249, 115, 22);
    doc.rect(0, 32, pageWidth, 2.5, 'F');
    y = 44;

    // Project badge
    const projTitle = r.nomeProjeto || 'Estimativa de Custo';
    doc.setFillColor(240, 244, 248);
    doc.roundedRect(margin, y, pageWidth - margin * 2, 10, 2, 2, 'F');
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(100, 116, 139);
    doc.text(`RELATÓRIO  ·  ${projTitle.toUpperCase()}`, margin + 5, y + 7);
    y += 16;

    // === HERO: CUSTO TOTAL ===
    doc.setFillColor(30, 58, 95);
    doc.roundedRect(margin, y, pageWidth - margin * 2, 38, 4, 4, 'F');
    doc.setTextColor(200, 210, 225);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text('CUSTO TOTAL ESTIMADO', margin + 10, y + 11);
    doc.setTextColor(249, 115, 22);
    doc.setFontSize(26);
    doc.text(formatBRL(r.total), margin + 10, y + 27);

    // Custo por m² (right)
    doc.setTextColor(200, 210, 225);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text('CUSTO POR M²', pageWidth - margin - 10, y + 11, { align: 'right' });
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    const custoM2 = r.total / (parseFloat(r.areaTotal) || 1);
    doc.text(`${formatBRL(custoM2)}/m²`, pageWidth - margin - 10, y + 25, { align: 'right' });
    doc.setTextColor(200, 210, 225);
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.text(`(CUB ${formatBRL(r.cubBase)} + BDI ${r.bdiPercent}%)`, pageWidth - margin - 10, y + 32, { align: 'right' });
    y += 46;

    // === KPI CARDS ===
    const kpiWidth = (pageWidth - margin * 2 - 12) / 4;
    const kpiHeight = 22;
    const kpiGap = 4;
    const kpis = [
      { label: 'ÁREA TOTAL', value: `${parseFloat(r.areaTotal).toLocaleString('pt-BR')} m²`, color: [249, 115, 22] },
      { label: 'PADRÃO', value: `${r.tipoLabel || 'Normal'}`, color: [30, 58, 95] },
      { label: 'ESTADO', value: `${r.estadoNome || r.estadoSigla}`, color: [30, 58, 95] },
      { label: 'BDI', value: `${r.bdiPercent}%`, color: [16, 185, 129] },
    ];
    kpis.forEach((kpi, i) => {
      const kpiX = margin + i * (kpiWidth + kpiGap);
      doc.setFillColor(248, 250, 252);
      doc.setDrawColor(226, 232, 240);
      doc.roundedRect(kpiX, y, kpiWidth, kpiHeight, 2, 2, 'FD');
      doc.setFillColor(...kpi.color);
      doc.rect(kpiX, y, kpiWidth, 2.5, 'F');
      doc.setFontSize(6);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(148, 163, 184);
      doc.text(kpi.label, kpiX + kpiWidth / 2, y + 9, { align: 'center' });
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(30, 41, 59);
      doc.text(kpi.value, kpiX + kpiWidth / 2, y + 17, { align: 'center' });
    });
    y += kpiHeight + 10;

    // === COMPOSIÇÃO POR ETAPAS (bars) ===
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30, 58, 95);
    doc.text('Composição de Custos por Etapa', margin, y);
    y += 7;

    const topEtapas = [...etapasCalc].sort((a, b) => b.percentual - a.percentual).slice(0, 6);
    const barColors = [[249,115,22],[30,58,95],[16,185,129],[59,130,246],[139,92,246],[180,83,9]];
    const barMaxWidth = pageWidth - margin * 2 - 80;
    const maxPct = Math.max(...topEtapas.map(e => e.percentual), 1);

    topEtapas.forEach((etapa, i) => {
      const barWidth = (etapa.percentual / maxPct) * barMaxWidth;
      const barColor = barColors[i % barColors.length];
      doc.setFontSize(7);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(71, 85, 105);
      doc.text(etapa.nome.length > 28 ? etapa.nome.substring(0, 28) + '...' : etapa.nome, margin, y + 4);
      doc.setFillColor(241, 245, 249);
      doc.roundedRect(margin + 52, y, barMaxWidth, 6, 1.5, 1.5, 'F');
      if (barWidth > 2) {
        doc.setFillColor(...barColor);
        doc.roundedRect(margin + 52, y, barWidth, 6, 1.5, 1.5, 'F');
      }
      doc.setFontSize(7);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(30, 41, 59);
      doc.text(`${etapa.percentual}%`, pageWidth - margin - 30, y + 4, { align: 'right' });
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(6);
      doc.setTextColor(100, 116, 139);
      doc.text(formatBRL(etapa.valor), pageWidth - margin, y + 4, { align: 'right' });
      y += 9;
    });

    if (etapasCalc.length > 6) {
      const demais = etapasCalc.filter((_, i) => !topEtapas.includes(etapasCalc[i]));
      const demaisPct = demais.reduce((s, e) => s + e.percentual, 0);
      const demaisVal = demais.reduce((s, e) => s + e.valor, 0);
      doc.setFontSize(6);
      doc.setFont('helvetica', 'italic');
      doc.setTextColor(148, 163, 184);
      doc.text(`+ ${demais.length} etapas (${demaisPct.toFixed(1)}%) — ${formatBRL(demaisVal)}`, margin, y + 3);
      y += 7;
    }
    y += 6;

    // === AMBIENTES IDENTIFICADOS (chips) ===
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30, 58, 95);
    doc.text('Ambientes Identificados', margin, y);
    y += 7;

    let chipX = margin;
    materiaisCalc.forEach((m) => {
      const label = `${m.nome}  ·  ${m.area} m²`;
      const chipW = Math.min(doc.getStringUnitWidth(label) * 7 / doc.internal.scaleFactor + 8, 70);
      if (chipX + chipW > pageWidth - margin) { chipX = margin; y += 11; checkPage(30); }
      doc.setFillColor(241, 245, 249);
      doc.setDrawColor(203, 213, 225);
      doc.roundedRect(chipX, y, chipW, 8, 2, 2, 'FD');
      doc.setFontSize(6.5);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(30, 58, 95);
      doc.text(label, chipX + 4, y + 5.5);
      chipX += chipW + 3;
    });
    y += 14;

    // === DADOS TÉCNICOS (rodapé pag 1) ===
    doc.setFillColor(248, 250, 252);
    doc.setDrawColor(226, 232, 240);
    doc.roundedRect(margin, y, pageWidth - margin * 2, 16, 2, 2, 'FD');
    doc.setFontSize(6.5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 116, 139);
    doc.text(`Tipo: ${r.tipoCodigo || ''} — ${r.tipoLabel || ''}  |  CUB Base: ${formatBRL(r.cubBase)}/m²  |  Ref: ${r.dataRef || ''}`, margin + 5, y + 6);
    const pa = r.padraoAcabamento || {};
    doc.text(`Acabamento: ${pa.tipoPiso || '—'}  |  Pé direito: ${pa.peDireito || '2,80m'}  |  Forro: ${pa.forro || '—'}  |  Rev: ${pa.revestimentoParedes || '—'}`, margin + 5, y + 12);
    y += 22;

    // Page break — detalhe começa na pag 2
    doc.addPage();
    y = 20;

    // Repeat mini header on detail pages
    doc.setFillColor(30, 58, 95);
    doc.rect(0, 0, pageWidth, 14, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text('QuantoCustaObra — Detalhamento', margin, 10);
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.text(orcId, pageWidth - margin, 10, { align: 'right' });
    doc.setFillColor(249, 115, 22);
    doc.rect(0, 14, pageWidth, 1.5, 'F');
    y = 22;

    // === AREAS TABLE ===
    sectionTitle('Detalhamento de Áreas');
    const areasRows = r.linhasAreas.map((l) => [l.item, l.calculo, formatBRL(l.valor)]);
    areasRows.push([
      { content: 'Subtotal Áreas', styles: { fontStyle: 'bold' } }, '',
      { content: formatBRL(r.subtotalAreas), styles: { fontStyle: 'bold', halign: 'right' } },
    ]);
    autoTable(doc, {
      startY: y, margin: { left: margin, right: margin },
      head: [['Item', 'Cálculo', 'Valor (R$)']],
      body: areasRows, theme: 'striped',
      headStyles: { fillColor: [30, 58, 95], fontSize: 8, fontStyle: 'bold' },
      bodyStyles: { fontSize: 8 },
      alternateRowStyles: { fillColor: [245, 247, 250] },
      columnStyles: { 2: { halign: 'right' } },
    });
    y = doc.lastAutoTable.finalY + 8;

    // === EXTRAS TABLE ===
    if (r.linhasExtras.length > 0) {
      sectionTitle('Itens Especiais');
      const extrasRows = r.linhasExtras.map((l) => [l.item, l.calculo, formatBRL(l.valor)]);
      extrasRows.push([
        { content: 'Subtotal Extras', styles: { fontStyle: 'bold' } }, '',
        { content: formatBRL(r.subtotalExtras), styles: { fontStyle: 'bold', halign: 'right' } },
      ]);
      autoTable(doc, {
        startY: y, margin: { left: margin, right: margin },
        head: [['Item Extra', 'Cálculo', 'Valor (R$)']],
        body: extrasRows, theme: 'striped',
        headStyles: { fillColor: [71, 85, 105], fontSize: 8, fontStyle: 'bold' },
        bodyStyles: { fontSize: 8 },
        alternateRowStyles: { fillColor: [245, 247, 250] },
        columnStyles: { 2: { halign: 'right' } },
      });
      y = doc.lastAutoTable.finalY + 8;
    }

    // === SUMMARY ===
    autoTable(doc, {
      startY: y, margin: { left: margin, right: margin },
      body: [
        ['Subtotal Geral', formatBRL(r.subtotalGeral)],
        [`BDI (${r.bdiPercent}%)`, formatBRL(r.bdiValor)],
      ],
      theme: 'plain', bodyStyles: { fontSize: 9 },
      columnStyles: { 0: { fontStyle: 'bold', cellWidth: 120 }, 1: { halign: 'right', fontStyle: 'bold' } },
    });
    y = doc.lastAutoTable.finalY + 8;

    // === ETAPAS TABLE ===
    sectionTitle('Breakdown por Etapas de Obra');
    const etapasRowsPDF = etapasCalc.map((e) => [e.nome, `${e.percentual}%`, formatBRL(e.valor)]);
    autoTable(doc, {
      startY: y, margin: { left: margin, right: margin },
      head: [['Etapa', '%', 'Valor Estimado (R$)']],
      body: etapasRowsPDF, theme: 'striped',
      headStyles: { fillColor: [249, 115, 22], fontSize: 8, fontStyle: 'bold' },
      bodyStyles: { fontSize: 7.5 },
      alternateRowStyles: { fillColor: [255, 251, 235] },
      columnStyles: { 1: { halign: 'center', cellWidth: 20 }, 2: { halign: 'right' } },
    });
    y = doc.lastAutoTable.finalY + 8;

    // === ACABAMENTOS POR CÔMODO (vertical card layout) ===
    checkPage(60);
    sectionTitle('Acabamentos e Quantitativos por Cômodo');

    materiaisCalc.forEach((m) => {
      checkPage(55);

      // Room header
      doc.setFillColor(219, 234, 254);
      doc.roundedRect(margin, y, pageWidth - margin * 2, 10, 2, 2, 'F');
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(30, 64, 175);
      doc.text(`${m.nome}`, margin + 4, y + 7);
      doc.text(`${m.area} m²`, pageWidth - margin - 4, y + 7, { align: 'right' });
      y += 14;

      // Build rows for this room
      const roomRows = [];

      // Piso
      roomRows.push([
        { content: 'Piso', styles: { fontStyle: 'bold' } },
        `${m.tipoPiso} — ${m.pisoM2} m²`,
        `Arg. colante: ${m.argamassaPisoKg} kg  |  Rejunte: ${m.rejuntePisoKg} kg`,
      ]);

      // Rodapé
      if (m.rodapeML > 0) {
        roomRows.push([
          { content: 'Rodapé', styles: { fontStyle: 'bold' } },
          `${m.tipoRodape} — ${m.rodapeML} ml`,
          '',
        ]);
      }

      // Revestimento parede
      if (m.areaPintura > 0 || m.areaRevCeramicoM2 > 0) {
        const revDesc = [];
        if (m.areaRevCeramicoM2 > 0) {
          revDesc.push(`${m.tipoRevParede} (${m.areaRevCeramicoM2} m²) — Arg: ${m.argamassaRevKg} kg | Rej: ${m.rejunteRevKg} kg`);
        }
        if (m.areaPintura > 0) {
          revDesc.push(`${m.tipoReboco || 'Massa corrida PVA'}: ${m.rebocoKgTotal} kg`);
          revDesc.push(`Tinta: ${m.tintaLitros} L  |  Selador: ${m.seladorLitros || 0} L`);
        }
        roomRows.push([
          { content: 'Parede', styles: { fontStyle: 'bold' } },
          revDesc[0] || '',
          revDesc.slice(1).join('  |  '),
        ]);
      }

      // Forro
      if (m.forroM2 > 0) {
        roomRows.push([
          { content: 'Forro', styles: { fontStyle: 'bold' } },
          `${m.tipoForro} — ${m.forroM2} m²`,
          '',
        ]);
      }

      // Alvenaria
      if (m.blocoUn > 0) {
        roomRows.push([
          { content: 'Alvenaria', styles: { fontStyle: 'bold' } },
          `Bloco cerâmico: ${m.blocoUn} un`,
          `Cimento: ${m.totalCimento} kg  |  Cal: ${m.totalCal} kg  |  Areia: ${m.totalAreia} kg`,
        ]);
      }

      // Esquadrias
      if ((m.totalPortas || 0) > 0 || (m.totalJanelas || 0) > 0) {
        const esqParts = [];
        if (m.esquadrias && m.esquadrias.length > 0) {
          m.esquadrias.forEach(e => {
            esqParts.push(`${e.quantidade}x ${e.tipo}${e.material ? ' (' + e.material + ')' : ''}${e.dimensoes ? ' ' + e.dimensoes : ''}`);
          });
        } else {
          if (m.totalPortas > 0) esqParts.push(`${m.totalPortas} porta(s)`);
          if (m.totalJanelas > 0) esqParts.push(`${m.totalJanelas} janela(s)`);
        }
        roomRows.push([
          { content: 'Esquadrias', styles: { fontStyle: 'bold' } },
          esqParts.join('  |  '),
          '',
        ]);
      }

      autoTable(doc, {
        startY: y, margin: { left: margin, right: margin },
        body: roomRows, theme: 'plain',
        bodyStyles: { fontSize: 7, cellPadding: 2 },
        columnStyles: { 0: { cellWidth: 28, fontStyle: 'bold' }, 1: { cellWidth: 70 } },
        didParseCell: (data) => {
          if (data.row.index % 2 === 0) {
            data.cell.styles.fillColor = [248, 250, 252];
          }
        },
      });
      y = doc.lastAutoTable.finalY + 6;
    });

    // === MTO CONSOLIDATED ===
    checkPage(80);
    sectionTitle('Material Take-Off — Insumos Consolidados');

    const totalCimento = sumField('totalCimento');
    const totalCal = sumField('totalCal');
    const totalAreia = sumField('totalAreia');

    // Insumos base highlight box
    doc.setFillColor(255, 247, 237);
    doc.setDrawColor(249, 115, 22);
    doc.roundedRect(margin, y, pageWidth - margin * 2, 24, 3, 3, 'FD');
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30, 58, 95);
    const thirdW = (pageWidth - margin * 2) / 3;
    doc.text(`Cimento: ${totalCimento.toLocaleString('pt-BR')} kg`, margin + 8, y + 9);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.text(`(${Math.ceil(totalCimento / 50)} sacos 50kg)`, margin + 8, y + 16);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.text(`Cal: ${totalCal.toLocaleString('pt-BR')} kg`, margin + thirdW + 8, y + 9);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.text(`(${Math.ceil(totalCal / 20)} sacos 20kg)`, margin + thirdW + 8, y + 16);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.text(`Areia: ${totalAreia.toLocaleString('pt-BR')} kg`, margin + thirdW * 2 + 8, y + 9);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.text(`(${(totalAreia / 1500).toFixed(1)} m³)`, margin + thirdW * 2 + 8, y + 16);
    y += 30;

    // MTO detail table
    checkPage(40);
    autoTable(doc, {
      startY: y, margin: { left: margin, right: margin },
      head: [['Material', 'Quantidade', 'Unidade']],
      body: [
        [{ content: 'ALVENARIA', colSpan: 3, styles: { fillColor: [16, 185, 129], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 8 } }],
        ['Bloco cerâmico 9×19×19', sumField('blocoUn').toLocaleString('pt-BR'), 'un'],
        ['Arg. assentamento — Cimento', sumNested('assentamento', 'cimento').toLocaleString('pt-BR'), 'kg'],
        ['Arg. assentamento — Cal', sumNested('assentamento', 'cal').toLocaleString('pt-BR'), 'kg'],
        ['Arg. assentamento — Areia', sumNested('assentamento', 'areia').toLocaleString('pt-BR'), 'kg'],
        [{ content: 'CHAPISCO (traço 1:3)', colSpan: 3, styles: { fillColor: [16, 185, 129], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 8 } }],
        ['Cimento', sumNested('chapisco', 'cimento').toLocaleString('pt-BR'), 'kg'],
        ['Areia', sumNested('chapisco', 'areia').toLocaleString('pt-BR'), 'kg'],
        [{ content: 'EMBOÇO (traço 1:2:8)', colSpan: 3, styles: { fillColor: [16, 185, 129], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 8 } }],
        ['Cimento', sumNested('emboco', 'cimento').toLocaleString('pt-BR'), 'kg'],
        ['Cal', sumNested('emboco', 'cal').toLocaleString('pt-BR'), 'kg'],
        ['Areia', sumNested('emboco', 'areia').toLocaleString('pt-BR'), 'kg'],
        [{ content: 'REVESTIMENTOS', colSpan: 3, styles: { fillColor: [59, 130, 246], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 8 } }],
        ['Piso total', sumField('pisoM2').toFixed(1), 'm²'],
        ['Argamassa colante (piso + parede)', (sumField('argamassaPisoKg') + sumField('argamassaRevKg')).toLocaleString('pt-BR'), 'kg'],
        ['Rejunte (piso + parede)', (sumField('rejuntePisoKg') + sumField('rejunteRevKg')).toLocaleString('pt-BR'), 'kg'],
        ['Rodapé', sumField('rodapeML').toFixed(1), 'ml'],
        [{ content: 'PINTURA E ACABAMENTO', colSpan: 3, styles: { fillColor: [139, 92, 246], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 8 } }],
        ['Selador', sumField('seladorLitros').toLocaleString('pt-BR'), 'L'],
        ['Tinta', sumField('tintaLitros').toLocaleString('pt-BR'), 'L'],
        [`Reboco (${materiaisCalc[0]?.tipoReboco || 'Massa corrida PVA'})`, sumField('rebocoKgTotal').toLocaleString('pt-BR'), 'kg'],
        ['Forro', sumField('forroM2').toFixed(1), 'm²'],
        [{ content: 'ESQUADRIAS', colSpan: 3, styles: { fillColor: [99, 102, 241], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 8 } }],
        ['Portas', sumField('totalPortas').toString(), 'un'],
        ['Janelas', sumField('totalJanelas').toString(), 'un'],
      ],
      theme: 'plain',
      headStyles: { fillColor: [30, 58, 95], fontSize: 8, fontStyle: 'bold', textColor: [255, 255, 255] },
      bodyStyles: { fontSize: 8 },
      columnStyles: { 0: { cellWidth: 110 }, 1: { halign: 'right', cellWidth: 35 }, 2: { halign: 'center', cellWidth: 20 } },
      didParseCell: (data) => {
        if (data.section === 'body' && data.cell.colSpan !== 3 && data.row.index % 2 === 1) {
          data.cell.styles.fillColor = [248, 250, 252];
        }
      },
    });
    y = doc.lastAutoTable.finalY + 8;

    // === COBERTURA TABLE ===
    if (coberturaCalc) {
      checkPage(50);
      sectionTitle(`Cobertura — ${coberturaCalc.tipo}`);
      const cobRows = [
        ['Área projeção', `${coberturaCalc.areaProjecao} m²`],
        [`Área real (incl. inclinação ${coberturaCalc.inclinacao}%)`, `${coberturaCalc.areaReal} m²`],
      ];
      if (coberturaCalc.telhasUn) cobRows.push(['Telhas (incl. 5% perdas)', `${coberturaCalc.telhasUn.toLocaleString('pt-BR')} un`]);
      if (coberturaCalc.chapasUn) cobRows.push(['Chapas', `${coberturaCalc.chapasUn} un`]);
      if (coberturaCalc.mantaM2) cobRows.push(['Manta asfáltica', `${coberturaCalc.mantaM2} m²`]);
      if (coberturaCalc.cumeiraML) cobRows.push(['Cumeeira', `${coberturaCalc.cumeiraML} ml`]);
      cobRows.push(['Calha', `${coberturaCalc.calhaML} ml`]);
      cobRows.push(['Rufo', `${coberturaCalc.rufoML} ml`]);
      if (coberturaCalc.estrutura === 'madeira') {
        cobRows.push(['Ripas', `${coberturaCalc.ripasML.toLocaleString('pt-BR')} ml`]);
        cobRows.push(['Caibros', `${coberturaCalc.caibrosML.toLocaleString('pt-BR')} ml`]);
        cobRows.push(['Terças', `${coberturaCalc.tercasML.toLocaleString('pt-BR')} ml`]);
      }
      if (coberturaCalc.estrutura === 'metalica') {
        cobRows.push(['Perfil metálico', `${coberturaCalc.perfilMetalicoKg.toLocaleString('pt-BR')} kg`]);
      }
      autoTable(doc, {
        startY: y, margin: { left: margin, right: margin },
        head: [['Item', 'Quantidade']],
        body: cobRows, theme: 'striped',
        headStyles: { fillColor: [180, 83, 9], fontSize: 8, fontStyle: 'bold', textColor: [255, 255, 255] },
        bodyStyles: { fontSize: 8 },
        alternateRowStyles: { fillColor: [255, 251, 235] },
        columnStyles: { 0: { cellWidth: 110 }, 1: { halign: 'right', cellWidth: 55 } },
      });
      y = doc.lastAutoTable.finalY + 8;
    }

    // === DISCLAIMERS ===
    checkPage(44);

    // Note: structural exclusion
    doc.setFillColor(239, 246, 255);
    doc.setDrawColor(147, 197, 253);
    doc.roundedRect(margin, y, pageWidth - margin * 2, 12, 2, 2, 'FD');
    doc.setFontSize(7);
    doc.setTextColor(30, 64, 175);
    doc.setFont('helvetica', 'bold');
    doc.text('Nota: Este quantitativo não inclui materiais de fundação, pilares, vigas e lajes (itens estruturais).', margin + 5, y + 8);
    y += 16;

    // Disclaimer
    doc.setFillColor(255, 251, 235);
    doc.setDrawColor(253, 230, 138);
    doc.roundedRect(margin, y, pageWidth - margin * 2, 20, 2, 2, 'FD');
    doc.setFontSize(7);
    doc.setTextColor(146, 64, 14);
    doc.setFont('helvetica', 'italic');
    doc.text('Este documento é uma estimativa paramétrica baseada no CUB (Custo Unitário Básico da Construção Civil).', margin + 5, y + 7);
    doc.text('Os valores e quantitativos são aproximados e não substituem um orçamento técnico detalhado elaborado por profissional habilitado.', margin + 5, y + 13);
    doc.text('Traços de referência: chapisco 1:3, emboço 1:2:8, argamassa de assentamento 1:2:8. Consulte sempre um engenheiro ou arquiteto.', margin + 5, y + 19);

    // === FOOTER ON ALL PAGES ===
    const lastPage = doc.internal.getNumberOfPages();
    for (let i = 1; i <= lastPage; i++) {
      doc.setPage(i);
      const ph = doc.internal.pageSize.getHeight();
      doc.setFillColor(30, 58, 95);
      doc.rect(0, ph - 16, pageWidth, 16, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.text('quantocustaobra.com.br | Referência CUB - Fonte: SINDUSCON', margin, ph - 6);
      doc.text(`${orcId}  |  Pág. ${i}/${lastPage}  |  ${new Date().toLocaleString('pt-BR')}`, pageWidth - margin, ph - 6, { align: 'right' });
    }

    const fileName = r.nomeProjeto
      ? `Orcamento-${r.nomeProjeto.replace(/\s+/g, '-')}-${new Date().toISOString().slice(0, 10)}.pdf`
      : `Orcamento-QCO-${new Date().toISOString().slice(0, 10)}.pdf`;
    doc.save(fileName);
  };

  if (!resultado) return null;

  return (
    <div className="min-h-screen bg-muted/30">
      <Header />
      <main className={`max-w-5xl mx-auto px-4 py-6 md:py-10 pt-36 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
        <div className="mb-6">
          <Link
            to="/orcamento-cub"
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors no-underline"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Voltar
          </Link>
        </div>

        <div className="bg-card rounded-2xl shadow-card overflow-hidden mb-6">
          {/* Total destaque */}
          <div className="bg-gradient-hero p-6 md:p-8 text-center">
            {resultado.nomeProjeto && (
              <p className="text-white/90 text-base font-semibold mb-1">{resultado.nomeProjeto}</p>
            )}
            <p className="text-white/70 text-sm font-medium mb-1">CUSTO TOTAL ESTIMADO</p>
            <p className="text-3xl md:text-4xl font-extrabold text-white">{formatBRL(resultado.total)}</p>
            <p className="text-white/70 text-xs mt-2">
              {resultado.tipoCodigo} - {resultado.tipoLabel} | {resultado.estadoNome} | BDI {resultado.bdiPercent}%
            </p>
          </div>

          {/* Padrão de Acabamento */}
          {resultado.padraoAcabamento && Object.values(resultado.padraoAcabamento).some(v => v) && (
            <div className="px-6 md:px-8 pt-4 pb-2">
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Padrão de Acabamento</h3>
              <div className="flex flex-wrap gap-2">
                {resultado.padraoAcabamento.tipoPiso && (
                  <span className="text-xs bg-muted px-2.5 py-1 rounded-lg text-foreground">Piso: {resultado.padraoAcabamento.tipoPiso}</span>
                )}
                {resultado.padraoAcabamento.peDireito && (
                  <span className="text-xs bg-muted px-2.5 py-1 rounded-lg text-foreground">Pé direito: {resultado.padraoAcabamento.peDireito}</span>
                )}
                {resultado.padraoAcabamento.forro && (
                  <span className="text-xs bg-muted px-2.5 py-1 rounded-lg text-foreground">Forro: {resultado.padraoAcabamento.forro}</span>
                )}
                {resultado.padraoAcabamento.revestimentoParedes && (
                  <span className="text-xs bg-muted px-2.5 py-1 rounded-lg text-foreground">Revestimento: {resultado.padraoAcabamento.revestimentoParedes}</span>
                )}
                {resultado.padraoAcabamento.alturaRevestimento && (
                  <span className="text-xs bg-muted px-2.5 py-1 rounded-lg text-foreground">Altura revest.: {resultado.padraoAcabamento.alturaRevestimento}</span>
                )}
              </div>
            </div>
          )}

          {/* Detalhamento */}
          <div className="p-6 md:p-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-foreground">Detalhamento</h2>
              {!plan.isFreePlan && (
                <div className="flex gap-2">
                  <PlanGate minPlan="basico" inline>
                    <button
                      onClick={handleExportPDF}
                      className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 cursor-pointer transition-colors flex items-center gap-1.5"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
                      Exportar PDF
                    </button>
                  </PlanGate>
                  <PlanGate minPlan="basico" inline>
                    <button
                      onClick={handleExportExcel}
                      className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 cursor-pointer transition-colors flex items-center gap-1.5"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                      Exportar Excel
                    </button>
                  </PlanGate>
                </div>
              )}
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-2 px-3 text-xs font-semibold text-muted-foreground uppercase">Item</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-muted-foreground uppercase">Cálculo</th>
                    <th className="text-right py-2 px-3 text-xs font-semibold text-muted-foreground uppercase">Valor</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-border/50 bg-muted">
                    <td colSpan={3} className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase">
                      CUB Base: {formatBRL(resultado.cubBase)}/m²
                    </td>
                  </tr>
                  {(plan.isFreePlan ? resultado.linhasAreas.slice(0, 2) : resultado.linhasAreas).map((l, i) => (
                    <tr key={`area-${i}`} className="border-b border-border/50 hover:bg-muted">
                      <td className="px-3 py-2.5 text-foreground">{l.item}</td>
                      <td className="px-3 py-2.5 text-muted-foreground text-xs">{l.calculo}</td>
                      <td className="px-3 py-2.5 text-right font-medium text-foreground">{formatBRL(l.valor)}</td>
                    </tr>
                  ))}

                  {plan.isFreePlan ? (
                    <tr>
                      <td colSpan={3} className="p-0">
                        <div className="bg-primary/5 border-y-2 border-primary/20 p-6 text-center">
                          <img src="/images/brand/bruno-principal.png" alt="Bruno" className="w-14 h-14 mx-auto mb-3 object-contain" />
                          <p className="text-foreground font-medium mb-2">
                            Sua obra tem {resultado.linhasAreas.length + resultado.linhasExtras.length} itens e vai custar aproximadamente <strong className="text-primary">{formatBRL(resultado.total)}</strong>.
                          </p>
                          <p className="text-sm text-muted-foreground mb-4">Para ver o detalhamento completo:</p>
                          <Link to="/checkout/basico" className="no-underline">
                            <button className="px-8 py-3 bg-gradient-cta text-white font-bold rounded-xl shadow-cta hover:opacity-90 transition-all text-sm">
                              Ver orçamento completo — R$ 49,90/mês
                            </button>
                          </Link>
                          <div className="mt-3">
                            <Link to="/planos" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                              Ou assine um plano →
                            </Link>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    <>
                      <tr className="border-b border-border bg-muted">
                        <td colSpan={2} className="px-3 py-2.5 font-semibold text-foreground">Subtotal Áreas</td>
                        <td className="px-3 py-2.5 text-right font-semibold text-foreground">{formatBRL(resultado.subtotalAreas)}</td>
                      </tr>

                      {resultado.linhasExtras.length > 0 && (
                        <>
                          <tr className="border-b border-border/50 bg-muted">
                            <td colSpan={3} className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase">Itens Extras</td>
                          </tr>
                          {resultado.linhasExtras.map((l, i) => (
                            <tr key={`extra-${i}`} className="border-b border-border/50 hover:bg-muted">
                              <td className="px-3 py-2.5 text-foreground">{l.item}</td>
                              <td className="px-3 py-2.5 text-muted-foreground text-xs">{l.calculo}</td>
                              <td className="px-3 py-2.5 text-right font-medium text-foreground">{formatBRL(l.valor)}</td>
                            </tr>
                          ))}
                          <tr className="border-b border-border bg-muted">
                            <td colSpan={2} className="px-3 py-2.5 font-semibold text-foreground">Subtotal Extras</td>
                            <td className="px-3 py-2.5 text-right font-semibold text-foreground">{formatBRL(resultado.subtotalExtras)}</td>
                          </tr>
                        </>
                      )}

                      <tr className="border-b border-border">
                        <td colSpan={2} className="px-3 py-2.5 font-semibold text-foreground">Subtotal Geral</td>
                        <td className="px-3 py-2.5 text-right font-semibold text-foreground">{formatBRL(resultado.subtotalGeral)}</td>
                      </tr>
                      <tr className="border-b border-border">
                        <td colSpan={2} className="px-3 py-2.5 text-foreground">BDI ({resultado.bdiPercent}%)</td>
                        <td className="px-3 py-2.5 text-right font-medium text-foreground">{formatBRL(resultado.bdiValor)}</td>
                      </tr>
                      <tr className="bg-primary/10">
                        <td colSpan={2} className="px-3 py-3 font-bold text-primary text-base">TOTAL ESTIMADO</td>
                        <td className="px-3 py-3 text-right font-bold text-primary text-base">{formatBRL(resultado.total)}</td>
                      </tr>
                    </>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Breakdown por Etapas */}
          {!plan.isFreePlan && (
            <div className="border-t border-border">
              <button
                onClick={() => setShowEtapas(!showEtapas)}
                className="w-full px-6 py-4 flex items-center justify-between hover:bg-muted/50 transition-colors cursor-pointer"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                    <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                  <div className="text-left">
                    <h3 className="text-sm font-bold text-foreground">Breakdown por Etapas de Obra</h3>
                    <p className="text-xs text-muted-foreground">17 etapas com custos estimados — Fonte: NBR 12721 / SINDUSCON</p>
                  </div>
                </div>
                <svg className={`w-5 h-5 text-muted-foreground transition-transform ${showEtapas ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {showEtapas && (
                <div className="px-6 pb-6">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-border">
                          <th className="text-left py-2 px-3 text-xs font-semibold text-muted-foreground uppercase">Etapa</th>
                          <th className="text-center py-2 px-3 text-xs font-semibold text-muted-foreground uppercase">%</th>
                          <th className="text-right py-2 px-3 text-xs font-semibold text-muted-foreground uppercase">Valor Estimado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {etapasCalc.map((e) => (
                          <tr key={e.id} className="border-b border-border/50 hover:bg-muted">
                            <td className="px-3 py-2.5 text-foreground">{e.nome}</td>
                            <td className="px-3 py-2.5 text-center">
                              <div className="flex items-center gap-2 justify-center">
                                <div className="w-16 h-1.5 bg-muted rounded-full overflow-hidden">
                                  <div className="h-full bg-primary rounded-full" style={{ width: `${Math.min(e.percentual * 6.67, 100)}%` }} />
                                </div>
                                <span className="text-xs text-muted-foreground w-8">{e.percentual}%</span>
                              </div>
                            </td>
                            <td className="px-3 py-2.5 text-right font-medium text-foreground">{formatBRL(e.valor)}</td>
                          </tr>
                        ))}
                        <tr className="bg-primary/10">
                          <td className="px-3 py-3 font-bold text-primary">TOTAL</td>
                          <td className="px-3 py-3 text-center font-bold text-primary">100%</td>
                          <td className="px-3 py-3 text-right font-bold text-primary">{formatBRL(resultado.total)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Quantitativo de Materiais */}
          {!plan.isFreePlan && (
            <div className="border-t border-border">
              <button
                onClick={() => setShowMateriais(!showMateriais)}
                className="w-full px-6 py-4 flex items-center justify-between hover:bg-muted/50 transition-colors cursor-pointer"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center">
                    <svg className="w-4 h-4 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                    </svg>
                  </div>
                  <div className="text-left">
                    <h3 className="text-sm font-bold text-foreground">Quantitativo de Materiais por Cômodo</h3>
                    <p className="text-xs text-muted-foreground">
                      {materiaisCalc.length > 0 && materiaisCalc[0].real
                        ? 'Baseado nos cômodos reais identificados na planta'
                        : 'Estimativa de piso, tinta e argamassa por ambiente'}
                    </p>
                  </div>
                </div>
                <svg className={`w-5 h-5 text-muted-foreground transition-transform ${showMateriais ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {showMateriais && (
                <div className="px-6 pb-6">
                  {/* Cards por cômodo */}
                  <div className="space-y-3 mb-6">
                    {materiaisCalc.map((m, i) => (
                      <div key={i} className="rounded-xl border border-border bg-muted/20 p-4">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="text-sm font-bold text-foreground">{m.nome}</h4>
                          <span className="text-xs font-medium text-muted-foreground bg-muted px-2.5 py-1 rounded-lg">{m.area} m²</span>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                          {/* Piso */}
                          <div>
                            <p className="text-muted-foreground mb-0.5">Piso</p>
                            <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                              (m.tipoPiso || '').includes('Porcelanato') ? 'bg-blue-100 text-blue-700' :
                              m.tipoPiso === 'Laminado' ? 'bg-amber-100 text-amber-700' :
                              m.tipoPiso === 'Vinílico' ? 'bg-purple-100 text-purple-700' :
                              'bg-green-100 text-green-700'
                            }`}>{m.tipoPiso}</span>
                            <p className="text-foreground font-medium mt-0.5">{m.pisoM2} m²</p>
                          </div>
                          {/* Pintura */}
                          <div>
                            <p className="text-muted-foreground mb-0.5">Pintura</p>
                            {m.areaPintura > 0 ? (
                              <>
                                <p className="text-foreground font-medium">Tinta: {m.tintaLitros} L</p>
                                <p className="text-foreground">Selador: {m.seladorLitros} L</p>
                                {m.tipoReboco && <p className="text-muted-foreground">{m.tipoReboco}: {m.rebocoKgTotal} kg</p>}
                              </>
                            ) : (
                              <p className="text-muted-foreground italic">Sem pintura (revestimento cerâmico piso ao teto)</p>
                            )}
                          </div>
                          {/* Revestimento */}
                          <div>
                            <p className="text-muted-foreground mb-0.5">Revestimento</p>
                            <p className="text-foreground font-medium">Arg. colante: {m.argamassaTotalKg} kg</p>
                            <p className="text-foreground">Rejunte: {m.rejunteTotalKg} kg</p>
                            {m.rodapeML > 0 && <p className="text-foreground">Rodapé: {m.rodapeML} ml</p>}
                          </div>
                          {/* Esquadrias */}
                          <div>
                            <p className="text-muted-foreground mb-0.5">Esquadrias</p>
                            {m.esquadrias && m.esquadrias.length > 0 ? (
                              m.esquadrias.map((e, j) => (
                                <p key={j} className="text-foreground">
                                  <span className="font-medium">{e.quantidade}x {e.tipo}</span>
                                  {e.material && <span className="text-muted-foreground"> {e.material}</span>}
                                  {e.dimensoes && <span className="text-muted-foreground"> ({e.dimensoes})</span>}
                                </p>
                              ))
                            ) : <p className="text-muted-foreground">—</p>}
                          </div>
                        </div>
                        {/* Acabamentos extras inline */}
                        {(m.bancada || m.pia || m.soleira || m.peitoril || (m.tipoForro && m.tipoForro !== 'Sem forro') || (m.tipoRevParede && m.tipoRevParede !== 'Sem revestimento')) && (
                          <div className="mt-2 pt-2 border-t border-border/30 flex flex-wrap gap-2 text-xs">
                            {m.bancada && <span className="bg-muted px-2 py-0.5 rounded-lg text-foreground">Bancada: {m.bancada}</span>}
                            {m.pia && <span className="bg-muted px-2 py-0.5 rounded-lg text-foreground">Pia: {m.pia}</span>}
                            {m.soleira && <span className="bg-muted px-2 py-0.5 rounded-lg text-foreground">Soleira: {m.soleira}</span>}
                            {m.peitoril && <span className="bg-muted px-2 py-0.5 rounded-lg text-foreground">Peitoril: {m.peitoril}</span>}
                            {m.tipoForro && m.tipoForro !== 'Sem forro' && <span className="bg-muted px-2 py-0.5 rounded-lg text-foreground">Forro: {m.tipoForro} ({m.forroM2} m²)</span>}
                            {m.tipoRevParede && m.tipoRevParede !== 'Sem revestimento' && <span className="bg-muted px-2 py-0.5 rounded-lg text-foreground">Rev. parede: {m.tipoRevParede}{m.alturaRev ? ` (${m.alturaRev})` : ''}</span>}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Resumo consolidado — material take-off */}
                  <div className="pt-4 border-t border-border">
                    <h4 className="text-sm font-bold text-foreground mb-3">Material Take-Off — Resumo consolidado</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">

                      {/* Card: Alvenaria */}
                      <div className="bg-muted/50 rounded-xl p-3">
                        <p className="text-xs font-bold text-muted-foreground uppercase mb-2">Alvenaria</p>
                        <div className="space-y-1 text-sm">
                          <div className="flex justify-between"><span className="text-foreground">Bloco cerâmico 9×19×19</span><span className="font-medium text-foreground">{materiaisCalc.reduce((s, m) => s + (m.blocoUn || 0), 0).toLocaleString('pt-BR')} un</span></div>
                        </div>
                        <p className="text-xs font-semibold text-muted-foreground mt-2 mb-1">Arg. assentamento (1:2:8)</p>
                        <div className="space-y-0.5 text-xs">
                          <div className="flex justify-between"><span className="text-foreground">Cimento</span><span className="font-medium">{materiaisCalc.reduce((s, m) => s + (m.assentamento?.cimento || 0), 0).toLocaleString('pt-BR')} kg</span></div>
                          <div className="flex justify-between"><span className="text-foreground">Cal</span><span className="font-medium">{materiaisCalc.reduce((s, m) => s + (m.assentamento?.cal || 0), 0).toLocaleString('pt-BR')} kg</span></div>
                          <div className="flex justify-between"><span className="text-foreground">Areia</span><span className="font-medium">{materiaisCalc.reduce((s, m) => s + (m.assentamento?.areia || 0), 0).toLocaleString('pt-BR')} kg</span></div>
                        </div>
                        <p className="text-xs font-semibold text-muted-foreground mt-2 mb-1">Chapisco (1:3)</p>
                        <div className="space-y-0.5 text-xs">
                          <div className="flex justify-between"><span className="text-foreground">Cimento</span><span className="font-medium">{materiaisCalc.reduce((s, m) => s + (m.chapisco?.cimento || 0), 0).toLocaleString('pt-BR')} kg</span></div>
                          <div className="flex justify-between"><span className="text-foreground">Areia</span><span className="font-medium">{materiaisCalc.reduce((s, m) => s + (m.chapisco?.areia || 0), 0).toLocaleString('pt-BR')} kg</span></div>
                        </div>
                        <p className="text-xs font-semibold text-muted-foreground mt-2 mb-1">Emboço (1:2:8)</p>
                        <div className="space-y-0.5 text-xs">
                          <div className="flex justify-between"><span className="text-foreground">Cimento</span><span className="font-medium">{materiaisCalc.reduce((s, m) => s + (m.emboco?.cimento || 0), 0).toLocaleString('pt-BR')} kg</span></div>
                          <div className="flex justify-between"><span className="text-foreground">Cal</span><span className="font-medium">{materiaisCalc.reduce((s, m) => s + (m.emboco?.cal || 0), 0).toLocaleString('pt-BR')} kg</span></div>
                          <div className="flex justify-between"><span className="text-foreground">Areia</span><span className="font-medium">{materiaisCalc.reduce((s, m) => s + (m.emboco?.areia || 0), 0).toLocaleString('pt-BR')} kg</span></div>
                        </div>
                      </div>

                      {/* Card: Revestimentos */}
                      <div className="bg-muted/50 rounded-xl p-3">
                        <p className="text-xs font-bold text-muted-foreground uppercase mb-2">Revestimentos</p>
                        <div className="space-y-1 text-sm">
                          <div className="flex justify-between"><span className="text-foreground">Piso (total)</span><span className="font-medium">{materiaisCalc.reduce((s, m) => s + m.pisoM2, 0).toFixed(1)} m²</span></div>
                          <div className="flex justify-between"><span className="text-foreground">Arg. colante (piso)</span><span className="font-medium">{materiaisCalc.reduce((s, m) => s + (m.argamassaPisoKg || 0), 0).toLocaleString('pt-BR')} kg</span></div>
                          <div className="flex justify-between"><span className="text-foreground">Rejunte (piso)</span><span className="font-medium">{materiaisCalc.reduce((s, m) => s + (m.rejuntePisoKg || 0), 0).toLocaleString('pt-BR')} kg</span></div>
                          <div className="flex justify-between"><span className="text-foreground">Rev. parede cerâmico</span><span className="font-medium">{materiaisCalc.reduce((s, m) => s + (m.areaRevCeramicoM2 || 0), 0).toFixed(1)} m²</span></div>
                          <div className="flex justify-between"><span className="text-foreground">Arg. colante (parede)</span><span className="font-medium">{materiaisCalc.reduce((s, m) => s + (m.argamassaRevKg || 0), 0).toLocaleString('pt-BR')} kg</span></div>
                          <div className="flex justify-between"><span className="text-foreground">Rejunte (parede)</span><span className="font-medium">{materiaisCalc.reduce((s, m) => s + (m.rejunteRevKg || 0), 0).toLocaleString('pt-BR')} kg</span></div>
                          <div className="flex justify-between"><span className="text-foreground">Rodapé</span><span className="font-medium">{materiaisCalc.reduce((s, m) => s + (m.rodapeML || 0), 0).toFixed(1)} ml</span></div>
                        </div>
                      </div>

                      {/* Card: Pintura + acabamento */}
                      <div className="bg-muted/50 rounded-xl p-3">
                        <p className="text-xs font-bold text-muted-foreground uppercase mb-2">Pintura e acabamento</p>
                        <div className="space-y-1 text-sm">
                          <div className="flex justify-between"><span className="text-foreground">Área de pintura</span><span className="font-medium">{materiaisCalc.reduce((s, m) => s + (m.areaPintura || 0), 0).toFixed(1)} m²</span></div>
                          <div className="flex justify-between"><span className="text-foreground">Selador</span><span className="font-medium">{materiaisCalc.reduce((s, m) => s + (m.seladorLitros || 0), 0)} L</span></div>
                          <div className="flex justify-between"><span className="text-foreground">Tinta</span><span className="font-medium">{materiaisCalc.reduce((s, m) => s + m.tintaLitros, 0)} L</span></div>
                          <div className="flex justify-between"><span className="text-foreground">Forro</span><span className="font-medium">{materiaisCalc.reduce((s, m) => s + (m.forroM2 || 0), 0).toFixed(1)} m²</span></div>
                        </div>
                        {materiaisCalc.some(m => m.tipoReboco) && (
                          <>
                            <p className="text-xs font-semibold text-muted-foreground mt-2 mb-1">Reboco ({materiaisCalc[0]?.tipoReboco || REBOCO_DEFAULT})</p>
                            <div className="space-y-0.5 text-xs">
                              <div className="flex justify-between"><span className="text-foreground">Total</span><span className="font-medium">{materiaisCalc.reduce((s, m) => s + (m.rebocoKgTotal || 0), 0).toLocaleString('pt-BR')} kg</span></div>
                            </div>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Card: Cobertura */}
                    {coberturaCalc && (
                      <div className="mt-3 bg-muted/50 rounded-xl p-3">
                        <p className="text-xs font-bold text-muted-foreground uppercase mb-2">Cobertura — {coberturaCalc.tipo}</p>
                        <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
                          <div className="flex justify-between"><span className="text-foreground">Área projeção</span><span className="font-medium">{coberturaCalc.areaProjecao} m²</span></div>
                          <div className="flex justify-between"><span className="text-foreground">Área real (incl. inclinação {coberturaCalc.inclinacao}%)</span><span className="font-medium">{coberturaCalc.areaReal} m²</span></div>
                          {coberturaCalc.telhasUn && <div className="flex justify-between"><span className="text-foreground">Telhas (incl. 5% perdas)</span><span className="font-medium">{coberturaCalc.telhasUn.toLocaleString('pt-BR')} un</span></div>}
                          {coberturaCalc.chapasUn && <div className="flex justify-between"><span className="text-foreground">Chapas</span><span className="font-medium">{coberturaCalc.chapasUn} un</span></div>}
                          {coberturaCalc.mantaM2 && <div className="flex justify-between"><span className="text-foreground">Manta asfáltica</span><span className="font-medium">{coberturaCalc.mantaM2} m²</span></div>}
                          {coberturaCalc.cumeiraML && <div className="flex justify-between"><span className="text-foreground">Cumeeira</span><span className="font-medium">{coberturaCalc.cumeiraML} ml</span></div>}
                          <div className="flex justify-between"><span className="text-foreground">Calha</span><span className="font-medium">{coberturaCalc.calhaML} ml</span></div>
                          <div className="flex justify-between"><span className="text-foreground">Rufo</span><span className="font-medium">{coberturaCalc.rufoML} ml</span></div>
                        </div>
                        {coberturaCalc.estrutura === 'madeira' && (
                          <>
                            <p className="text-xs font-semibold text-muted-foreground mt-2 mb-1">Madeiramento</p>
                            <div className="grid grid-cols-2 gap-x-6 gap-y-0.5 text-xs">
                              <div className="flex justify-between"><span className="text-foreground">Ripas</span><span className="font-medium">{coberturaCalc.ripasML.toLocaleString('pt-BR')} ml</span></div>
                              <div className="flex justify-between"><span className="text-foreground">Caibros</span><span className="font-medium">{coberturaCalc.caibrosML.toLocaleString('pt-BR')} ml</span></div>
                              <div className="flex justify-between"><span className="text-foreground">Terças</span><span className="font-medium">{coberturaCalc.tercasML.toLocaleString('pt-BR')} ml</span></div>
                            </div>
                          </>
                        )}
                        {coberturaCalc.estrutura === 'metalica' && (
                          <>
                            <p className="text-xs font-semibold text-muted-foreground mt-2 mb-1">Estrutura metálica</p>
                            <div className="text-xs">
                              <div className="flex justify-between"><span className="text-foreground">Perfil metálico</span><span className="font-medium">{coberturaCalc.perfilMetalicoKg.toLocaleString('pt-BR')} kg</span></div>
                            </div>
                          </>
                        )}
                      </div>
                    )}

                    {/* Totais consolidados de insumos base */}
                    <div className="mt-3 bg-primary/5 rounded-xl p-3 border border-primary/20">
                      <p className="text-xs font-bold text-primary uppercase mb-2">Insumos base consolidados (todas as etapas)</p>
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div className="text-center">
                          <p className="text-2xl font-bold text-foreground">{materiaisCalc.reduce((s, m) => s + (m.totalCimento || 0), 0).toLocaleString('pt-BR')}</p>
                          <p className="text-xs text-muted-foreground">kg de Cimento</p>
                          <p className="text-xs font-medium text-primary">({Math.ceil(materiaisCalc.reduce((s, m) => s + (m.totalCimento || 0), 0) / 50)} sacos de 50kg)</p>
                        </div>
                        <div className="text-center">
                          <p className="text-2xl font-bold text-foreground">{materiaisCalc.reduce((s, m) => s + (m.totalCal || 0), 0).toLocaleString('pt-BR')}</p>
                          <p className="text-xs text-muted-foreground">kg de Cal</p>
                          <p className="text-xs font-medium text-primary">({Math.ceil(materiaisCalc.reduce((s, m) => s + (m.totalCal || 0), 0) / 20)} sacos de 20kg)</p>
                        </div>
                        <div className="text-center">
                          <p className="text-2xl font-bold text-foreground">{materiaisCalc.reduce((s, m) => s + (m.totalAreia || 0), 0).toLocaleString('pt-BR')}</p>
                          <p className="text-xs text-muted-foreground">kg de Areia</p>
                          <p className="text-xs font-medium text-primary">({(materiaisCalc.reduce((s, m) => s + (m.totalAreia || 0), 0) / 1500).toFixed(1)} m³)</p>
                        </div>
                      </div>
                    </div>

                    {/* Esquadrias totais */}
                    <div className="mt-3 grid grid-cols-2 gap-3">
                      <div className="bg-muted/50 rounded-xl p-3">
                        <p className="text-xs font-bold text-muted-foreground uppercase mb-1">Portas</p>
                        <p className="text-lg font-bold text-foreground">{materiaisCalc.reduce((s, m) => s + (m.totalPortas || 0), 0)} un</p>
                      </div>
                      <div className="bg-muted/50 rounded-xl p-3">
                        <p className="text-xs font-bold text-muted-foreground uppercase mb-1">Janelas</p>
                        <p className="text-lg font-bold text-foreground">{materiaisCalc.reduce((s, m) => s + (m.totalJanelas || 0), 0)} un</p>
                      </div>
                    </div>
                  </div>

                  <p className="text-xs text-muted-foreground mt-4 italic">
                    {materiaisCalc.length > 0 && materiaisCalc[0].real
                      ? '* Quantitativos calculados a partir dos cômodos identificados na planta pela IA. Traços: chapisco 1:3, emboço 1:2:8, assentamento 1:2:8. Validar com projeto executivo.'
                      : '* Estimativa baseada na distribuição típica de ambientes. Quantidades reais dependem do projeto arquitetônico.'}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Rodape */}
          <div className="border-t border-border bg-muted px-6 py-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-xs text-muted-foreground">
              <div>
                <p>Referência: CUB {resultado.tipoCodigo} - {resultado.estadoNome} ({resultado.dataRef})</p>
                <p>Fonte: SINDUSCON | cub.org.br</p>
              </div>
              <p className="text-right italic">* Estimativa. Não substitui orçamento detalhado.</p>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

export default OrcamentoResultadoCub;
